import React, { useEffect, useRef } from 'react';
import { MdPrint, MdClose } from 'react-icons/md';
// pageShell.css owns .ui-btn, which the toolbar's Print/Close buttons
// use. Imported here rather than assumed: a document may be rendered
// outside a PageShell (a standalone print route, a portal), and
// without this its toolbar renders as two unstyled native buttons.
// CSS imports are deduplicated by the bundler, so this costs nothing
// on the normal path where PageShell already pulled it in.
import './pageShell.css';
import './printDocument.css';

/**
 * PrintDocument — the one printable-document shell for the whole app.
 *
 * WHY BROWSER PRINT AND NOT A PDF LIBRARY
 * jsPDF/html2canvas were rejected on purpose. jsPDF has no Arabic
 * shaping: it draws the code points in logical order with their
 * isolated glyph forms, so "مدرسة" comes out as disconnected,
 * left-to-right letters. html2canvas avoids that only by rasterising —
 * which gives a blurry, unsearchable, 3 MB "document" and still gets
 * RTL flex layouts wrong. The browser's own print engine already
 * shapes Arabic perfectly and every OS ships a "Save as PDF" printer,
 * so one Print button yields BOTH a real print and a real PDF, with
 * selectable text and no embedded font payload.
 *
 * HOW THE PRINT IS SCOPED
 * Printing must produce the document alone, never the sidebar, the
 * toolbar or the modal chrome around it. The rule in printDocument.css
 * is `display: none` on everything except the ancestor chain of
 * `.ui-doc-print-root`, which this component marks on itself when it
 * mounts. `display` rather than the `visibility` trick used by the
 * older sd-print-area CSS: a hidden-but-still-laid-out element keeps
 * occupying pages, which is how that approach produces a first page
 * that is entirely blank.
 *
 * PROPS CONTRACT
 *   title            string   — document title, printed under the letterhead
 *                               (e.g. "وثيقة طالب"). Required.
 *   subtitle         string?  — small line under the title (academic year…)
 *   docNumber        string|number? — reference/serial shown in the header
 *   issuedAt         string?  — ISO date to print as the issue date;
 *                               defaults to today.
 *   size             'a4' | 'a5'  — paper size. 'a5' is for receipts,
 *                               which are traditionally half a sheet.
 *   schoolName       string?  — defaults to 'بوابة المتميزون'
 *   schoolSubtitle   string?  — line under the school name (e.g. directorate)
 *   logoUrl          string?  — when absent, a bordered placeholder box
 *                               is printed so the layout does not shift
 *                               once a real logo is configured.
 *   signatures       Array<{ title: string, name?: string }>?
 *                            — signature blocks printed in the footer.
 *   footerNote       string?  — small print line above the signatures
 *                               (legal note, contact details…)
 *   showToolbar      boolean  — show the on-screen Print/Close bar.
 *                               Default true; the bar never prints.
 *   onClose          function? — renders a Close button in the toolbar.
 *   autoPrint        boolean  — fire the print dialog once on mount.
 *                               Use for a "print straight away" flow
 *                               with no preview. Default false.
 *   children         node     — the document body.
 *
 * The component is purely presentational: it fetches nothing and owns
 * no data state. The page loads the data and passes it in.
 */
export default function PrintDocument({
  title,
  subtitle,
  docNumber,
  issuedAt,
  size = 'a4',
  schoolName = 'بوابة المتميزون',
  schoolSubtitle,
  logoUrl,
  signatures,
  footerNote,
  showToolbar = true,
  onClose,
  autoPrint = false,
  children,
}) {
  const rootRef = useRef(null);

  /* Mark the ancestor chain so the print stylesheet can hide everything
   * else.
   *
   * printDocument.css hides `body > *` and re-shows only
   * `.ui-doc-print-ancestor`. CSS alone cannot express "the ancestors
   * of this element" (there is no parent selector that works across
   * arbitrary depth), and the chain is genuinely arbitrary: a document
   * may be rendered on a page, inside a Modal's overlay, or inside a
   * React portal, each giving a different set of wrappers between
   * <body> and the sheet. So JS walks up once on mount and tags them,
   * and untags on unmount so a later print of a DIFFERENT document is
   * not polluted by a stale chain. */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const chain = [];
    // Start at the PARENT, never at the sheet itself. The hide rule is
    // `.ui-doc-print-ancestor > *:not(.ui-doc-print-ancestor):not(.ui-doc-print-root)`,
    // so tagging the sheet too made every direct child of the sheet —
    // the letterhead, the title block, the body, the signatures — match
    // that selector and get `display: none`. The document then printed
    // as an empty bordered box: a blank page with a few stray rules on
    // it, which is exactly the reported symptom.
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      node.classList.add('ui-doc-print-ancestor');
      chain.push(node);
    }
    return () => chain.forEach((node) => node.classList.remove('ui-doc-print-ancestor'));
  }, []);

  // A one-shot guard: React 18 StrictMode mounts effects twice in dev,
  // and two window.print() calls open two dialogs stacked on each other.
  const printedRef = useRef(false);
  useEffect(() => {
    if (!autoPrint || printedRef.current) return undefined;
    printedRef.current = true;
    // Deferred a frame: printing during commit captures the layout
    // before fonts and the document's own CSS have applied, which
    // prints an unstyled, Latin-fallback page.
    const id = window.requestAnimationFrame(() => window.print());
    return () => window.cancelAnimationFrame(id);
  }, [autoPrint]);

  return (
    <div className={`ui-doc-wrap ui-doc-${size}`} dir="rtl">
      {showToolbar && (
        <div className="ui-doc-toolbar ui-doc-noprint">
          <button type="button" className="ui-btn ui-btn-primary" onClick={() => window.print()} data-guide="doc-print">
            <MdPrint size={16} /> طباعة
          </button>
          {/* Said plainly, because most staff do not know that the print
              dialog is also how you produce a PDF. */}
          <span className="ui-doc-toolbar-hint">
            اختر «حفظ بصيغة PDF» من نافذة الطباعة للحصول على نسخة إلكترونية
          </span>
          {onClose && (
            <button type="button" className="ui-btn ui-btn-secondary ui-doc-toolbar-close" onClick={onClose}>
              <MdClose size={16} /> إغلاق
            </button>
          )}
        </div>
      )}

      <article className="ui-doc ui-doc-print-root" ref={rootRef} dir="rtl">
        <header className="ui-doc-head">
          <div className="ui-doc-brand">
            {logoUrl
              ? <img className="ui-doc-logo" src={logoUrl} alt="" />
              : <div className="ui-doc-logo ui-doc-logo-placeholder" aria-hidden="true">شعار</div>}
            <div className="ui-doc-brand-text">
              <h1 className="ui-doc-school">{schoolName}</h1>
              {schoolSubtitle && <p className="ui-doc-school-sub">{schoolSubtitle}</p>}
            </div>
          </div>

          <div className="ui-doc-meta">
            {docNumber != null && docNumber !== '' && (
              <div className="ui-doc-meta-row">
                <span className="ui-doc-meta-label">الرقم</span>
                <span className="ui-doc-num">{docNumber}</span>
              </div>
            )}
            <div className="ui-doc-meta-row">
              <span className="ui-doc-meta-label">التاريخ</span>
              <span className="ui-doc-num">{formatDate(issuedAt) || formatDate(new Date())}</span>
            </div>
          </div>
        </header>

        <div className="ui-doc-title-block">
          <h2 className="ui-doc-title">{title}</h2>
          {subtitle && <p className="ui-doc-subtitle">{subtitle}</p>}
        </div>

        <div className="ui-doc-body">{children}</div>

        <footer className="ui-doc-foot">
          {signatures && signatures.length > 0 && (
            <div className="ui-doc-signatures">
              {signatures.map((sig) => (
                <div className="ui-doc-sign" key={sig.title}>
                  <span className="ui-doc-sign-title">{sig.title}</span>
                  <span className="ui-doc-sign-line" aria-hidden="true" />
                  {sig.name && <span className="ui-doc-sign-name">{sig.name}</span>}
                </div>
              ))}
            </div>
          )}
          {footerNote && <p className="ui-doc-foot-note">{footerNote}</p>}
        </footer>
      </article>
    </div>
  );
}

/* ----------------------------------------------------------------------
 * Shared building blocks. Documents compose these instead of writing
 * their own markup, so every document prints with the same rules, the
 * same rhythm and the same page-break behaviour.
 * ------------------------------------------------------------------- */

/** A titled band of the document (هوية الطالب، بيانات الأسرة…). */
export function DocSection({ title, children }) {
  return (
    <section className="ui-doc-section">
      {title && <h3 className="ui-doc-section-title">{title}</h3>}
      {children}
    </section>
  );
}

/** Two-column label/value grid — the standard layout for record fields. */
export function DocFieldGrid({ columns = 2, children }) {
  return <div className={`ui-doc-grid ui-doc-grid-${columns}`}>{children}</div>;
}

/**
 * One label/value pair.
 *
 * A missing value prints a long dash, NEVER the row's absence: an
 * official record is read as a checklist, and a silently dropped
 * "الرقم الوطني" row reads as if the field did not exist rather than
 * as "not recorded". `numeric` isolates the value's bidi direction —
 * see DocNumber for why that is not optional.
 */
export function DocField({ label, value, numeric = false, wide = false }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={`ui-doc-field${wide ? ' ui-doc-field-wide' : ''}`}>
      <span className="ui-doc-field-label">{label}</span>
      <span className={`ui-doc-field-value${empty ? ' is-empty' : ''}${numeric && !empty ? ' ui-doc-num' : ''}`}>
        {empty ? '—' : value}
      </span>
    </div>
  );
}

/**
 * A number, date, phone or ID inside Arabic text.
 *
 * Without an explicit isolate these render in the wrong visual order:
 * an RTL paragraph reorders the run's neighbouring digits and
 * punctuation, so "2026-09-23" can display as "23-09-2026" and a phone
 * number's leading + jumps to the far end. Same fix as
 * .user-code-value in pages/users.css, factored out so no document has
 * to remember it.
 */
export function DocNumber({ children }) {
  return <span className="ui-doc-num">{children}</span>;
}

/**
 * A status/result word.
 *
 * Deliberately not a coloured pill. School printers are monochrome, so
 * a green "ناجح" chip and a red "راسب" chip come out as two identical
 * grey boxes. Meaning is carried by the WORD plus a border weight that
 * survives greyscale, never by fill colour alone.
 */
export function DocMark({ tone = 'neutral', children }) {
  return <span className={`ui-doc-mark ui-doc-mark-${tone}`}>{children}</span>;
}

/**
 * A document table (marks, instalments…).
 *
 * Wrapped rather than bare <table> so the print rules that keep a row
 * from splitting across pages and repeat the header on page two live
 * in one place.
 */
export function DocTable({ head, children, compact = false }) {
  return (
    <table className={`ui-doc-table${compact ? ' is-compact' : ''}`}>
      {head && <thead>{head}</thead>}
      <tbody>{children}</tbody>
    </table>
  );
}

/* ----------------------------------------------------------------------
 * Formatting helpers. Exported because the documents and the pages that
 * host them must agree on how a date or an amount looks.
 * ------------------------------------------------------------------- */

/**
 * ISO timestamp -> YYYY-MM-DD.
 *
 * Sliced as text rather than parsed through Date: the API sends
 * '2026-09-23T14:05:00.000Z' and `new Date(...)` would shift it into
 * the viewer's local zone, which in Damascus (UTC+3) prints the
 * NEXT day for anything recorded after 21:00. A document's date must
 * match what was stored.
 */
export function formatDate(value) {
  if (!value) return '';
  if (value instanceof Date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return String(value).slice(0, 10);
}

/** The app's money format — matches Payments.js and StudentDirectory.js.
 *  US dollars, "$1,250": the sign stuck to the number keeps the same
 *  order inside Arabic text and in an LTR-isolated figure alike. */
export function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString('en-US')}`;
}

const ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
const TEENS = ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const HUNDREDS = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

/** 0–999 in Arabic words, units before tens as Arabic orders them. */
function underThousand(n) {
  const parts = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  if (h) parts.push(HUNDREDS[h]);
  if (rest >= 10 && rest < 20) {
    parts.push(TEENS[rest - 10]);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    // Arabic says "خمسة وأربعون" — the unit first, joined by و.
    if (o && t >= 2) parts.push(`${ONES[o]} و${TENS[t]}`);
    else if (o) parts.push(ONES[o]);
    else if (t >= 2) parts.push(TENS[t]);
  }
  return parts.join(' و');
}

/**
 * The form a scale word ("ألف"/"مليون") takes after a count.
 *
 * Arabic inflects the counted noun by the count, and — this is the
 * part that is easy to get wrong — by the count's LAST TWO DIGITS, not
 * its magnitude:
 *   1              -> ألف            (the bare singular)
 *   2              -> ألفان          (the dual)
 *   3–10           -> ثلاثة آلاف     (the plural)
 *   11–99          -> خمسة عشر ألفاً (singular, accusative/tamyīz)
 *   100, 200, 300… -> مائة ألف       (singular again, genitive)
 * So 100000 is "مائة ألف", never "مائة ألفاً", while 150000 is
 * "مائة وخمسون ألفاً" because ITS last two digits are 50. Collapsing
 * the last two cases into one accusative branch is the usual bug and
 * produces a phrase a clerk will reject on a receipt.
 */
function scaleWord(count, [singular, dual, plural, accusative]) {
  if (count === 1) return singular;
  if (count === 2) return dual;
  if (count >= 3 && count <= 10) return `${underThousand(count)} ${plural}`;
  const lastTwo = count % 100;
  // A round hundred (100, 1200, 3000…) reverts to the bare singular.
  if (lastTwo === 0) {
    // ...and "مئتان" drops its nūn before the counted noun (iḍāfa):
    // 200,000 is "مئتا ألف", not "مئتان ألف". The dual is the only
    // number word in this range that changes shape this way.
    return `${underThousand(count).replace(/مئتان$/, 'مئتا')} ${singular}`;
  }
  return `${underThousand(count)} ${accusative}`;
}

/**
 * An amount written out in Arabic words.
 *
 * Real receipts print the sum twice, in digits and in words, because a
 * digit is trivially altered after the fact (a 1 becomes a 7, a zero is
 * appended) while a written phrase is not. Every Syrian school receipt
 * carries the "فقط ... لا غير" wrapper for exactly that reason, so this
 * is a correctness feature of the document, not decoration.
 *
 * Covers 0 to 999,999,999, which is far beyond any school fee. Anything
 * larger returns an empty string, and PaymentReceiptDoc then prints the
 * digits alone rather than a wrong phrase.
 */
export function amountInArabicWords(amount) {
  const n = Math.floor(Math.abs(Number(amount) || 0));
  if (n === 0) return 'صفر';
  if (n > 999999999) return '';

  const groups = [
    { value: Math.floor(n / 1000000), words: ['مليون', 'مليونان', 'ملايين', 'مليوناً'] },
    { value: Math.floor((n % 1000000) / 1000), words: ['ألف', 'ألفان', 'آلاف', 'ألفاً'] },
    { value: n % 1000, words: null },
  ];

  const parts = [];
  for (const g of groups) {
    if (!g.value) continue;
    parts.push(g.words ? scaleWord(g.value, g.words) : underThousand(g.value));
  }
  return parts.join(' و');
}
